import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Paper,
  Skeleton,
  Button,
  Drawer,
  IconButton,
  Slider,
  FormControlLabel,
  Checkbox,
  Divider,
  Select,
  MenuItem,
  Rating,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useCart } from "../../context/CartContext";
import VendorSponsorshipCard from "./VendorSponsorshipCard";
import Popover from "@mui/material/Popover";
import LinearProgress from "@mui/material/LinearProgress";
import { getPolicyBadges } from "../../utils/policyBadges";
import SlickSlider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
// import ZoomImage from "../../components/ZoomImage"; // Zoom functionality commented out

// Helper to format time from "HH:mm" to "h:mm AM/PM"
const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
};

// Helper to format business hours into a compact readable string
const formatBusinessHours = (businessHours) => {
  if (!businessHours || !businessHours.days) return null;
  
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  // Group consecutive days with same schedule
  const groups = [];
  let currentGroup = null;
  
  days.forEach((day, idx) => {
    const config = businessHours.days[day] || { open: false };
    
    const schedule = config.open
      ? `${formatTime(config.startTime)} - ${formatTime(config.endTime)}`
      : 'Closed';
    
    if (!currentGroup || currentGroup.schedule !== schedule) {
      currentGroup = { schedule, days: [idx] };
      groups.push(currentGroup);
    } else {
      currentGroup.days.push(idx);
    }
  });
  
  // Build readable string
  const parts = groups.map(group => {
    const dayIndices = group.days;
    if (dayIndices.length === 1) {
      return `${dayLabels[dayIndices[0]]}: ${group.schedule}`;
    } else {
      const firstDay = dayLabels[dayIndices[0]];
      const lastDay = dayLabels[dayIndices[dayIndices.length - 1]];
      return `${firstDay}-${lastDay}: ${group.schedule}`;
    }
  });
  
  return parts.join(', ');
};

// ================= STYLE CONSTANTS =================
const STYLES = {
  // Product Card Styles (Amazon-inspired) - Predictable blocks
  productCard: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    cursor: "pointer",
    position: "relative",
    overflow: "hidden",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      boxShadow: {
        xs: "0 4px 8px rgba(0, 0, 0, 0.08)",
        sm: "0 6px 16px rgba(0, 0, 0, 0.12)"
      },
      transform: { xs: 'none', sm: "translateY(-6px) scale(1.01)" },
      borderColor: "#a6a6a6",
      zIndex: 1,
    },
  },

  // Out of Stock Badge
  outOfStockBadge: {
    position: "absolute",
    top: 10,
    left: 10,
    bgcolor: "#fdecea",
    color: "#b71c1c",
    px: 1,
    borderRadius: 1,
    fontSize: 12,
    fontWeight: 600,
  },

  // Add to Cart Button Styles (Amazon-style)
  addToCartButton: {
    bgcolor: "#ffd814",
    color: "#0f1111",
    fontWeight: 500,
    textTransform: "none",
    borderRadius: "10px",
    p: "6px 8px",
    minHeight: 32,
    fontSize: 12,
    border: "1px solid #fcd200",
    transition: "all 0.15s ease",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    "&:hover": {
      bgcolor: "#f7ca00",
      borderColor: "#f2c200",
      boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
      transform: "translateY(-1px)",
    },
    "&:active": {
      bgcolor: "#f0c200",
      transform: "translateY(0)",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
  },

  // Clear Filters Button Styles
  clearFiltersButton: {
    mt: 2,
    borderColor: "#ff9900",
    color: "#ff9900",
    fontWeight: 600,
    textTransform: "none",
    "&:hover": {
      bgcolor: "#fff3e0",
      borderColor: "#ff9900",
    },
  },

  // Verified Badge Styles
  verifiedBadge: {
    bgcolor: "#e6f4ea",
    color: "#1e8e3e",
    px: 1,
    borderRadius: 1,
    fontSize: 12,
    fontWeight: 600,
  },

  // Follow Button Styles
  followButton: {
    borderColor: '#D5D9D9',
    color: '#0F1111',
    fontWeight: 600,
    borderRadius: 8,
    textTransform: 'none',
    px: 2,
    py: 1,
    '&:hover': {
      backgroundColor: '#FFCE12',
      borderColor: '#D5D9D9',
    },
  },

  // Tab Styles
  tab: {
    cursor: "pointer",
    pb: 1,
    borderBottom: "2px solid #000",
    fontWeight: 600,
  },

  tabInactive: {
    cursor: "pointer",
    pb: 1,
    borderBottom: "none",
    fontWeight: 400,
  },

  // Price Color
  priceColor: "#B12704",
};

// ================= COMPONENT CONSTANTS =================
const GRID_BREAKPOINTS = {
  xs: 6,    // 2 per row on mobile (Amazon shows 2)
  sm: 4,    // 3 per row on small
  md: 3,    // 4 per row on medium
  lg: 2.4,  // 5 per row on large (fractional for CSS Grid)
};

// For Material-UI Grid which requires integer values, we'll use this fallback
const GRID_BREAKPOINTS_MUI = {
  xs: 6,
  sm: 4,
  md: 3,
  lg: 2,
};

const MAX_TITLE_LENGTH = 40;

// ================= REUSABLE COMPONENTS =================

// FilterSidebar Component
const FilterSidebar = ({
  priceRange,
  setPriceRange,
  priceStats,
  categories,
  selectedCategories,
  setSelectedCategories,
  selectedRating,
  setSelectedRating,
  setSortBy,
  setFilterDrawerOpen
}) => (
  <Paper
    elevation={1}
    sx={{
      width: "100%",
      maxWidth: 220,
      p: 1.5,
      bgcolor: "background.paper",
      borderRadius: 2,
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
      <Typography variant="h6" fontWeight={700}>
        Filters
      </Typography>
      <IconButton
        size="small"
        onClick={() => setFilterDrawerOpen(false)}
        sx={{ display: { xs: "block", md: "none" } }}
      >
        <CloseIcon />
      </IconButton>
    </Box>

    {/* PRICE FILTER */}
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        Price
      </Typography>
      <Slider
        value={priceRange}
        onChange={(e, newValue) => {
          if (Array.isArray(newValue)) {
            setPriceRange(newValue);
          }
        }}
        min={priceStats.min}
        max={priceStats.max}
        step={100}
        valueLabelDisplay="auto"
        marks={[
          { value: priceStats.min, label: `₹${Number(priceStats.min).toLocaleString("en-IN")}` },
          { value: priceStats.max, label: `₹${Number(priceStats.max).toLocaleString("en-IN")}` },
        ]}
        sx={{
          mx: 'auto',
          width: '80%', // Reduce the length of the slider
          display: 'block',
          '& .MuiSlider-mark': {
            width: 32,
            height: 32,
            backgroundColor: 'transparent',
            '&:before': {
              display: 'none',
            },
            '& .MuiSlider-mark-label': {
              top: -10,
              fontSize: 10,
              whiteSpace: 'nowrap',
              transform: 'translateX(-50%)',
            },
          },
        }}
      />
    </Box>

    {/* CATEGORY FILTER */}
    {categories.length > 0 && (
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Category
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {categories.map((cat) => (
            <FormControlLabel
              key={cat}
              control={
                <Checkbox
                  size="small"
                  checked={selectedCategories.includes(cat)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCategories([...selectedCategories, cat]);
                    } else {
                      setSelectedCategories(
                        selectedCategories.filter((c) => c !== cat)
                      );
                    }
                  }}
                />
              }
              label={<Typography sx={{ fontSize: 13 }}>{cat}</Typography>}
            />
          ))}
        </Box>
      </Box>
    )}

    <Divider sx={{ mb: 2 }} />

    {/* RATING FILTER */}
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        Rating
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {[4, 3, 2, 1].map((rating) => (
          <FormControlLabel
            key={rating}
            control={
              <Checkbox
                size="small"
                checked={selectedRating === rating}
                onChange={(e) => {
                  setSelectedRating(e.target.checked ? rating : 0);
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: 13 }}>
                ⭐ {rating}+ Star{rating !== 1 ? "s" : ""}
              </Typography>
            }
          />
        ))}
      </Box>
    </Box>

    {/* CLEAR FILTERS */}
    <Button
      fullWidth
      variant="outlined"
      sx={STYLES.clearFiltersButton}
      onClick={() => {
        setPriceRange([priceStats.min, priceStats.max]);
        setSelectedCategories([]);
        setSelectedRating(0);
        setSortBy("relevance");
      }}
    >
      Clear All Filters
    </Button>
  </Paper>
);

// RatingPopover Component
const RatingPopover = ({ open, anchorEl, vendor, setOpen }) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={() => setOpen(false)}
    disableRestoreFocus
    disableAutoFocus
    disableEnforceFocus
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "left",
    }}
    TransitionProps={{
      timeout: { enter: 0, exit: 0 }
    }}
    PaperProps={{
      sx: {
        p: 2,
        width: 250,
        borderRadius: 2,
      }
    }}
  >
    <Typography fontWeight={700} sx={{ mb: 1 }}>
      {vendor.rating?.avg?.toFixed(1)} out of 5
    </Typography>

    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {vendor.rating?.totalReviews} global ratings
    </Typography>

    {[5, 4, 3, 2, 1].map((star) => (
      <Box key={star} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography sx={{ width: 30, fontSize: 12 }}>
          {star}★
        </Typography>
        <LinearProgress
          variant="determinate"
          value={vendor.rating?.distribution?.[star] || 0}
          sx={{
            flex: 1,
            height: 8,
            borderRadius: 5,
            mx: 1
          }}
        />
        <Typography sx={{ fontSize: 12 }}>
          {vendor.rating?.distribution?.[star] || 0}%
        </Typography>
      </Box>
    ))}
  </Popover>
);

// ProductGrid Component
const ProductGrid = ({ products, ProductCard }) => (
  <Grid container spacing={2} alignItems="stretch">
    {products.map((p) => (
      <Grid item {...GRID_BREAKPOINTS_MUI} key={p._id} sx={{ display: "flex" }}>
        {ProductCard(p)}
      </Grid>
    ))}
  </Grid>
);

const VendorStore = () => {
  const { addToCart } = useCart();
  const { vendorId } = useParams();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const [tab, setTab] = useState("products");

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Filter & Sort States
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedRating, setSelectedRating] = useState(0);
  const [sortBy, setSortBy] = useState("relevance");

  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);

  const handleMouseEnter = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setOpen(false);
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch store data
        const storeRes = await api.get(`/vendor/${vendorId}/store`);
        setVendor(storeRes.data.vendor);
        setProducts(storeRes.data.products);

        // Fetch featured products
        const featuredRes = await api.get(`/vendor/${vendorId}/store/featured`);
        setFeaturedProducts(featuredRes.data.products || []);
      } catch (error) {
        console.error("Error fetching store data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [vendorId]);

  // Carousel logic for store gallery
  const handleNextImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === (vendor?.storefrontImages?.length || 0) - 1 ? 0 : prevIndex + 1
    );
  }, [vendor?.storefrontImages?.length]);

  const handlePrevImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? (vendor?.storefrontImages?.length || 0) - 1 : prevIndex - 1
    );
  }, [vendor?.storefrontImages?.length]);
  useEffect(() => {
    if (vendor?.storefrontImages?.length > 1) {
      const interval = setInterval(handleNextImage, 5000); // Change image every 5 seconds
      return () => clearInterval(interval);
    }
  }, [vendor?.storefrontImages?.length, handleNextImage]);

  const refreshVendor = useCallback(async () => {
    const res = await api.get(`/vendor/${vendorId}/store`);
    setVendor(res.data.vendor);
  }, [vendorId]);
  const about = vendor?.store?.about || {};
  // Extract unique categories from products
  const categories = useMemo(() => {
    const cats = new Set();
    products.forEach((p) => {
      if (p.category) cats.add(p.category);
    });
    return Array.from(cats).sort();
  }, [products]);

  // Extract price range from products
  const priceStats = useMemo(() => {
    if (products.length === 0) return { min: 0, max: 100000 };
    const prices = products.map((p) => p.price);
    return {
      min: Math.floor(Math.min(...prices)),
      max: Math.ceil(Math.max(...prices)),
    };
  }, [products]);

  // Filter & Sort Logic
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = products.filter((p) => {
      // Price filter
      if (p.price < priceRange[0] || p.price > priceRange[1])
        return false;

      // Category filter
      if (
        selectedCategories.length > 0 &&
        !selectedCategories.includes(p.category)
      )
        return false;

      // Rating filter
      if (selectedRating > 0) {
        const productRating = p.rating?.avg || 0;
        if (productRating < selectedRating) return false;
      }

      return true;
    });

    // Sort
    const sorted = [...filtered];
    switch (sortBy) {
      case "price_low":
        sorted.sort((a, b) => a.price - b.price);
        break;
      case "price_high":
        sorted.sort((a, b) => b.price - a.price);
        break;
      case "rating":
        sorted.sort(
          (a, b) => (b.rating?.avg || 0) - (a.rating?.avg || 0)
        );
        break;
      case "newest":
        sorted.sort(
          (a, b) =>
            new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );
        break;
      case "popularity":
        sorted.sort(
          (a, b) => (b.reviewCount || 0) - (a.reviewCount || 0)
        );
        break;
      default: // relevance
        break;
    }

    return sorted;
  }, [products, priceRange, selectedCategories, selectedRating, sortBy]);

  // Reset filters on tab change
  useEffect(() => {
    setPriceRange([priceStats.min, priceStats.max]);
    setSelectedCategories([]);
    setSelectedRating(0);
    setSortBy("relevance");
  }, [tab, priceStats]);

  const handleAddToCart = useCallback((e, product) => {
    e.stopPropagation();

    addToCart({
      id: product._id,
      title: product.title,
      price: product.price,
      imageUrl: product.images?.[0],
      qty: 1,
      vendorId: product.vendorId,
      vendorName: product.vendorName,
    });
  }, [addToCart]);

  /* ================= PRODUCT CARD ================= */
  const ProductCard = useMemo(() => (p) => {
    const stock = p?.stock ?? 0;
    const inStock = stock > 0;
    const badges = getPolicyBadges(p.policies);

    return (
      <Card
        sx={{
          borderRadius: 2,
          position: "relative",
          transition: "0.2s",
          "&:hover": { boxShadow: 6 },
        }}
        onClick={() => navigate(`/product/${p._id}`)}
        onMouseEnter={() => setHoveredId(p._id)}
        onMouseLeave={() => setHoveredId(null)}
      >
        <CardMedia
          component="img"
          image={p.images?.[0] || "/placeholder-product.png"}
          alt={p.title}
          sx={{
            width: "100%",
            objectFit: "contain",
          }}
        />

        {/* OUT OF STOCK */}
        {!inStock && (
          <Box sx={STYLES.outOfStockBadge}>
            Out of Stock
          </Box>
        )}

        {/* CONTENT - Amazon-like predictable blocks */}
        <CardContent
          sx={{
            pt: 1.5,
            pb: "12px !important",
          }}
        >
          {/* TITLE - Fixed height (2 lines) */}
          <Tooltip title={p.title || ""} arrow placement="top">
            <Typography
              sx={{
                fontSize: { xs: 12, sm: 14 },
                fontWeight: 600,
                lineHeight: 1.2,
                height: 36, // Fixed height for 2 lines
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                textOverflow: "ellipsis",
                overflowWrap: "break-word",
                wordBreak: "break-word",
                whiteSpace: "normal",
                maxWidth: "min(100%, 40ch)", // Wrap after ~40 characters
              }}
            >
              {p.title || ""}
            </Typography>
          </Tooltip>

          {/* RATING - Fixed height row */}
          <Box sx={{ height: 20, display: "flex", alignItems: "center", mt: 0.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, mr: 0.5 }}>
              {p?.rating?.avg?.toFixed(1) || "0.0"}
            </Typography>
            <Rating
              value={p?.rating?.avg || 0}
              precision={0.5}
              size="small"
              readOnly
            />
            <Typography sx={{ fontSize: 11, color: "text.secondary", ml: 0.5 }}>
              ({p?.rating?.count || p?.ratingCount || 0})
            </Typography>
          </Box>

          {/* PRICE - Fixed height */}
          <Typography
            fontWeight={700}
            sx={{ height: 22, display: "flex", alignItems: "center" }}
          >
            ₹{Number(p.price || 0).toLocaleString("en-IN")}
          </Typography>

          {/* BUTTON - Fixed height, always present */}
          <Button
            size="small"
            variant={inStock ? "contained" : "outlined"}
            fullWidth
            disabled={!inStock}
            sx={{
              mt: "auto",
              mb: 0.5,
              height: { xs: 28, sm: 32 },
              maxWidth: '100%',
              ...STYLES.addToCartButton,
              ...(!inStock && {
                bgcolor: "transparent",
                color: "text.secondary",
                borderColor: "#e0e0e0",
                "&:hover": {
                  bgcolor: "transparent",
                  borderColor: "#e0e0e0",
                },
              }),
            }}
            onClick={inStock ? (e) => handleAddToCart(e, p) : undefined}
          >
            {inStock ? "Add to Cart" : "Out of Stock"}
          </Button>
        </CardContent>
      </Card>
    );
  }, [navigate, setHoveredId, hoveredId, handleAddToCart, getPolicyBadges]);

  if (loading) return <Skeleton variant="rectangular" height={300} />;
  if (!vendor) return <Typography>Store not found</Typography>;

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh", overflowX: 'hidden', boxSizing: 'border-box' }}>

      {/* ================= BANNER ================= */}
      <Box
        sx={{
          height: { xs: 150, sm: 180, md: 220 },
          backgroundImage: `url(${vendor.banner || "/default-banner.jpg"})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* ================= HEADER ================= */}
      <Paper
        sx={{
          maxWidth: 1200,
          mx: "auto",
          p: { xs: 2, sm: 3 },
          mt: -6,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "flex-start", md: "center" }, gap: 2 }}>
          <Box
            sx={{
              width: { xs: 70, sm: 80, md: 90 },
              height: { xs: 70, sm: 80, md: 90 },
              borderRadius: 2,
              overflow: "hidden",
              flexShrink: 0,
              bgcolor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={vendor.logo || "/placeholder.png"}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>

            {/* 🏷 NAME + BADGES */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: 1 }}>
              <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                {vendor.store?.businessName || vendor.name}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {/* 📢 SPONSORED BADGE */}
                <VendorSponsorshipCard
                  vendorId={vendor._id}
                  sponsorshipStatus={vendor.sponsorshipStatus}
                  onApplied={refreshVendor}
                />

                {/* ✔️ VERIFIED BADGE */}
                {vendor.isVerified && (
                  <Box sx={STYLES.verifiedBadge}>
                    ✔ Verified
                  </Box>
                )}


              </Box>
            </Box>

            {/* 📝 TAGLINE */}
            <Typography color="text.secondary">
              {vendor.tagline || "Trusted seller"}
            </Typography>

            {/* ⭐ RATING */}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <Box
                onClick={handleMouseEnter}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>
                  ⭐ {vendor.rating?.avg?.toFixed(1) || "0.0"}
                </Typography>
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 18,
                    transition: "transform 0.2s",
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                onClick={() => navigate(`/vendor/${vendor._id}/reviews`)}
                sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              >
                ({vendor.rating?.totalReviews || 0} reviews)
              </Typography>
            </Box>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={() => setOpen(false)}
              disableRestoreFocus
              disableAutoFocus
              disableEnforceFocus
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              TransitionProps={{
                timeout: { enter: 0, exit: 0 }
              }}
              PaperProps={{
                sx: {
                  p: 2,
                  width: 250,
                  borderRadius: 2,
                }
              }}
            >
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                {vendor.rating?.avg?.toFixed(1)} out of 5
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {vendor.rating?.totalReviews} global ratings
              </Typography>

              {[5, 4, 3, 2, 1].map((star) => (
                <Box key={star} sx={{ display: "flex", alignItems: "center", mb: 1 }}>

                  <Typography sx={{ width: 30, fontSize: 12 }}>
                    {star}★
                  </Typography>

                  <LinearProgress
                    variant="determinate"
                    value={vendor.rating?.distribution?.[star] || 0}
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: 5,
                      mx: 1
                    }}
                  />

                  <Typography sx={{ fontSize: 12 }}>
                    {vendor.rating?.distribution?.[star] || 0}%
                  </Typography>

                </Box>
              ))}
            </Popover>

            {/* 📧 CONTACT */}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Contact: {vendor.email}
            </Typography>

            {/* 🕒 BUSINESS HOURS */}
            {vendor.store?.businessHours && (
              <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.875rem' }}>
                Hours: {formatBusinessHours(vendor.store.businessHours)}
              </Typography>
            )}
          </Box>

          {/* ❤️ FOLLOW BUTTON */}
          <Button
            variant="outlined"
            sx={STYLES.followButton}
          >
            Follow
          </Button>
        </Box>
      </Paper>

      {/* ================= TABS ================= */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          mt: 2,
          display: "flex",
          gap: { xs: 1, sm: 3 },
          px: { xs: 1, sm: 2 },
          borderBottom: "1px solid #ddd",
          overflowX: 'auto',
        }}
      >
        {["home", "products", "store policy", "about"].map((t) => (
          <Typography
            key={t}
            onClick={() => setTab(t)}
            sx={{
              ...(tab === t ? STYLES.tab : STYLES.tabInactive),
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              whiteSpace: 'nowrap',
            }}
          >
            {t.toUpperCase()}
          </Typography>
        ))}
      </Box>

      {/* ================= HOME ================= */}
      {tab === "home" && (
        <Box sx={{ maxWidth: { xs: '100%', sm: 1200 }, mx: "auto", mt: 3, px: { xs: 1.5, sm: 2 }, pb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Featured Products
          </Typography>

          {featuredProducts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No featured products yet. Check back soon!
              </Typography>
            </Box>
          ) : (
            <Box
          sx={{
            columnCount: { xs: 2, sm: 3, md: 4, lg: 5 },
            columnGap: 1,
          }}
        >
          {featuredProducts.map((p) => (
            <Box key={p._id} sx={{ breakInside: "avoid", mb: 1 }}>
              <ProductCard {...p} />
            </Box>
          ))}
        </Box>
          )}
        </Box>
      )}

      {/* ================= PRODUCTS (WITH FILTERS) ================= */}
      {tab === "products" && (
        <Box sx={{ maxWidth: { xs: '100%', md: 1400 }, mx: "auto", mt: 3, px: { xs: 1.5, sm: 2 }, pb: 3 }}>
          {/* TOOLBAR: Filter Button + Sort Dropdown */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              mb: 3,
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <IconButton
                onClick={() => setFilterDrawerOpen(true)}
                sx={{
                  display: { xs: "flex", md: "none" },
                  borderRadius: 1,
                  bgcolor: "#f5f5f5",
                }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                {filteredAndSortedProducts.length} products
              </Typography>
            </Box>

            {/* SORT DROPDOWN */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 1,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                Sort by:
              </Typography>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                size="small"
                sx={{
                  width: { xs: "100%", sm: 200 },
                  bgcolor: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: 1,
                }}
              >
                <MenuItem value="relevance">Relevance</MenuItem>
                <MenuItem value="price_low">Price: Low to High</MenuItem>
                <MenuItem value="price_high">Price: High to Low</MenuItem>
                <MenuItem value="rating">Highest Rated</MenuItem>
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="popularity">Most Popular</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* MAIN CONTENT: Sidebar + Products */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 5,
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                flex: "0 0 220px",
                position: "sticky",
                top: 110,
                alignSelf: "flex-start",
              }}
            >
              <FilterSidebar
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                priceStats={priceStats}
                categories={categories}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                selectedRating={selectedRating}
                setSelectedRating={setSelectedRating}
                setSortBy={setSortBy}
                setFilterDrawerOpen={setFilterDrawerOpen}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              {filteredAndSortedProducts.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    No products match your filters
                  </Typography>
                  <Button
                    sx={{ mt: 2, color: "#ff9900" }}
                    onClick={() => {
                      setPriceRange([priceStats.min, priceStats.max]);
                      setSelectedCategories([]);
                      setSelectedRating(0);
                      setSortBy("relevance");
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    columnCount: { xs: 2, sm: 3, md: 4, lg: 5 },
                    columnGap: 1,
                  }}
                >
                  {filteredAndSortedProducts.map((p) => (
                    <Box key={p._id} sx={{ breakInside: "avoid", mb: 1 }}>
                      {ProductCard(p)}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* FILTER DRAWER (Mobile) */}
      <Drawer
        anchor="left"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <Box sx={{ width: 300, p: 2 }}>
          <FilterSidebar
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            priceStats={priceStats}
            categories={categories}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            selectedRating={selectedRating}
            setSelectedRating={setSelectedRating}
            setSortBy={setSortBy}
            setFilterDrawerOpen={setFilterDrawerOpen}
          />
        </Box>
      </Drawer>
      {/* ================= STORE POLICY ================= */}
      {tab === "store policy" && (
        <Box sx={{ maxWidth: 900, mx: "auto", mt: 3, px: { xs: 1, sm: 2 } }}>
          {vendor?.store?.policies ? (
            <Box>
              {vendor.store.policies.returnPolicy && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Return Policy
                  </Typography>
                  <Typography color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                    {vendor.store.policies.returnPolicy}
                  </Typography>
                </Box>
              )}

              {vendor.store.policies.shippingPolicy && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Shipping Policy
                  </Typography>
                  <Typography color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                    {vendor.store.policies.shippingPolicy}
                  </Typography>
                </Box>
              )}

              {vendor.store.policies.warranty && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Warranty
                  </Typography>
                  <Typography color="text.secondary">
                    {vendor.store.policies.warranty}
                  </Typography>
                </Box>
              )}

              {vendor.store.policies.processingTime && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Processing Time
                  </Typography>
                  <Typography color="text.secondary">
                    {vendor.store.policies.processingTime.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Typography>
                </Box>
              )}

              {!vendor.store.policies.returnPolicy && !vendor.store.policies.shippingPolicy && !vendor.store.policies.warranty && !vendor.store.policies.processingTime && (
                <Typography color="text.secondary">
                  No policies available.
                </Typography>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">
              No policies available.
            </Typography>
          )}
        </Box>
      )}

      {/* ================= ABOUT ================= */}
      {tab === "about" && (
        <Box sx={{ maxWidth: 900, mx: "auto", mt: 3, px: { xs: 1, sm: 2 } }}>

          {/* 🏢 ABOUT HEADER */}
          <Typography variant="h5" fontWeight={700} gutterBottom>
            About {vendor.store?.businessName || vendor.name}
          </Typography>

          {/* 📖 DESCRIPTION (SHORT) */}
          {vendor.description && (
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {vendor.description}
            </Typography>
          )}

          {/* 📜 BUSINESS STORY */}
          {about.businessStory && (
            <>
              <Typography variant="h6" gutterBottom>
                Our Story
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                {about.businessStory}
              </Typography>
            </>
          )}

          {/* 🎯 MISSION */}
          {about.mission && (
            <>
              <Typography variant="h6" gutterBottom>
                Our Mission
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                {about.mission}
              </Typography>
            </>
          )}

          {/* 💎 VALUES */}
          {about.values?.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Our Values
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                {about.values.map((v, i) => (
                  <Box
                    key={i}
                    sx={{
                      px: 2,
                      py: 0.5,
                      bgcolor: "#fff3e0",
                      borderRadius: 2,
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {v}
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* ⏳ EXPERIENCE */}
          {about.yearsOfExperience && (
            <Typography sx={{ mb: 3 }}>
              <strong>{about.yearsOfExperience}+ years of experience</strong>
            </Typography>
          )}

          {/* 🎓 CERTIFICATIONS */}
          {about.certifications?.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Certifications
              </Typography>

              <Box sx={{ mb: 3 }}>
                {about.certifications.map((c, i) => (
                  <Typography key={i} color="text.secondary">
                    • {c.title} ({c.issuedBy} - {c.year})
                  </Typography>
                ))}
              </Box>
            </>
          )}

          {/* 👥 TEAM */}
          {about.team?.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Meet Our Team
              </Typography>

              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {about.team.map((member, i) => (
                  <Grid item xs={6} md={4} key={i}>
                    <Box textAlign="center">
                      <img
                        src={member.photo || "/placeholder.png"}
                        alt={member.name}
                        style={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          objectFit: "cover",
                          marginBottom: 8,
                        }}
                      />
                      <Typography fontWeight={600}>
                        {member.name}
                      </Typography>
                      <Typography fontSize={13} color="text.secondary">
                        {member.role}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {/* 🖼 STORE GALLERY */}
          {vendor.storefrontImages?.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Store Gallery
              </Typography>

              <Box
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 3,
                  "& .slick-slider": {
                    position: "relative",
                  },
                  "& .slick-slide": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  "& .slick-slide img": {
                    width: "100%",
                    height: { xs: 200, sm: 300, md: 400 },
                    objectFit: "cover",
                    display: "block",
                  },
                  "& .slick-prev, & .slick-next": {
                    zIndex: 1,
                  },
                  "& .slick-prev:before, & .slick-next:before": {
                    color: "#ffffff",
                    fontSize: "24px",
                    textShadow: "0 0 10px rgba(0,0,0,0.5)",
                  },
                  "& .slick-dots": {
                    bottom: "15px",
                  },
                  "& .slick-dots li button:before": {
                    color: "white",
                    fontSize: "10px",
                    textShadow: "0 0 5px rgba(0,0,0,0.5)",
                  },
                  "& .slick-dots li.slick-active button:before": {
                    color: "#ff9900",
                  },
                }}
              >
                <SlickSlider
                  dots={true}
                  infinite={true}
                  speed={500}
                  slidesToShow={1}
                  slidesToScroll={1}
                  autoplay={true}
                  autoplaySpeed={4000}
                  pauseOnHover={true}
                  arrows={true}
                  prevArrow={<CustomPrevArrow />}
                  nextArrow={<CustomNextArrow />}
                >
                  {vendor.storefrontImages.map((image, index) => (
                    <Box key={index} sx={{ outline: "none" }}>
                      <img
                        src={image}
                        alt={`Store gallery ${index + 1}`}
                        style={{
                          width: "100%",
                          height: { xs: 200, sm: 300, md: 400 },
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </Box>
                  ))}
                </SlickSlider>
              </Box>
            </>
          )}

          {/* 📍 ADDRESS */}
          {vendor.address && (
            <>
              <Typography variant="h6" gutterBottom>
                Address
              </Typography>

              <Typography color="text.secondary">
                {vendor.address.addressLine1}, {vendor.address.city},{" "}
                {vendor.address.state} - {vendor.address.pincode}
              </Typography>
            </>
          )}

        </Box>
      )}
    </Box>
  );
};

// Custom Previous Arrow Component
const CustomPrevArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <Box
      onClick={onClick}
      sx={{
        position: "absolute",
        left: "15px",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        bgcolor: "rgba(0, 0, 0, 0.5)",
        color: "white",
        transition: "all 0.3s ease",
        "&:hover": {
          bgcolor: "rgba(0, 0, 0, 0.8)",
          transform: "translateY(-50%) scale(1.1)",
        },
      }}
    >
      <ArrowBackIosIcon sx={{ fontSize: 24 }} />
    </Box>
  );
};

// Custom Next Arrow Component
const CustomNextArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <Box
      onClick={onClick}
      sx={{
        position: "absolute",
        right: "15px",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        bgcolor: "rgba(0, 0, 0, 0.5)",
        color: "white",
        transition: "all 0.3s ease",
        "&:hover": {
          bgcolor: "rgba(0, 0, 0, 0.8)",
          transform: "translateY(-50%) scale(1.1)",
        },
      }}
    >
      <ArrowForwardIosIcon sx={{ fontSize: 24 }} />
    </Box>
  );
};

export default VendorStore;
